import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { usePageTitle } from "../lib/appShell";
import { useHeightUnit } from "../lib/units";

const t = (v) => (v && typeof v === "object" ? v.default : v) || "";
const pct = (v) => (v == null ? "—" : (v * 100).toFixed(1));
const age = (d) => { if (!d) return ""; const b = new Date(d), n = new Date(); let a = n.getFullYear() - b.getFullYear(); if (n < new Date(n.getFullYear(), b.getMonth(), b.getDate())) a -= 1; return a; };
const season = (s) => { const x = String(s); return `${x.slice(0, 4)}-${x.slice(6, 8)}`; };
const POS = { C: "Center", L: "Left Wing", R: "Right Wing", D: "Defense", G: "Goalie" };

const SK_COLS = [["gamesPlayed", "GP"], ["goals", "G"], ["assists", "A"], ["points", "P"], ["plusMinus", "+/-"], ["pim", "PIM"], ["powerPlayGoals", "PPG"], ["shots", "S"], ["shootingPctg", "S%", pct], ["avgToi", "TOI"]];
const G_COLS = [["gamesPlayed", "GP"], ["gamesStarted", "GS"], ["wins", "W"], ["losses", "L"], ["otLosses", "OT"], ["goalsAgainstAvg", "GAA", (v) => (v == null ? "—" : Number(v).toFixed(2))], ["savePctg", "SV%", (v) => (v == null ? "—" : Number(v).toFixed(3).replace(/^0/, ""))], ["shutouts", "SO"]];

function Featured({ label, st, goalie }) {
    if (!st) return null;
    const items = goalie
        ? [["GP", st.gamesPlayed], ["W", st.wins], ["GAA", st.goalsAgainstAvg != null ? Number(st.goalsAgainstAvg).toFixed(2) : "—"], ["SV%", st.savePctg != null ? Number(st.savePctg).toFixed(3).replace(/^0/, "") : "—"], ["SO", st.shutouts]]
        : [["GP", st.gamesPlayed], ["G", st.goals], ["A", st.assists], ["P", st.points], ["+/-", st.plusMinus]];
    return (
        <div className="rounded-2xl border border-white/10 bg-white/[0.03] p-4" data-testid={`nhl-player-featured-${label.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="text-[10px] font-black uppercase tracking-wider text-white/45 mb-3">{label}</div>
            <div className="grid grid-cols-5 gap-2">
                {items.map(([k, v]) => (
                    <div key={k} className="text-center"><div className="text-2xl font-black text-white tabular-nums">{v ?? "—"}</div><div className="text-[10px] font-bold uppercase text-white/45">{k}</div></div>
                ))}
            </div>
        </div>
    );
}

function StatTable({ rows, cols, first, testid }) {
    return (
        <div className="overflow-x-auto rounded-2xl border border-white/10" data-testid={testid}>
            <table className="w-full text-sm tabular-nums">
                <thead><tr className="bg-white/[0.04] text-[10px] font-black uppercase tracking-wider text-white/50">
                    {first.map(([, l]) => <th key={l} className="px-3 py-2 text-left whitespace-nowrap">{l}</th>)}
                    {cols.map(([, l]) => <th key={l} className="px-3 py-2 text-center whitespace-nowrap">{l}</th>)}
                </tr></thead>
                <tbody>{rows.map((r, i) => (
                    <tr key={i} className="border-t border-white/[0.06] hover:bg-white/[0.03]">
                        {first.map(([f, l]) => <td key={l} className="px-3 py-2 whitespace-nowrap text-white/85">{f(r)}</td>)}
                        {cols.map(([k, l, fmt]) => <td key={l} className="px-3 py-2 text-center text-white/75">{fmt ? fmt(r[k]) : (r[k] ?? "—")}</td>)}
                    </tr>
                ))}</tbody>
            </table>
        </div>
    );
}

export default function NhlPlayer() {
    const { playerId } = useParams();
    const [unit] = useHeightUnit();
    const [scope, setScope] = useState("nhl");
    const { data: p, isLoading, isError } = useQuery({ queryKey: ["nhl-player", playerId], queryFn: () => api.get(`/nhl/player/${playerId}`).then((r) => r.data), staleTime: 5 * 60 * 1000 });
    usePageTitle(p?.firstName ? `${p.firstName.default} ${p.lastName?.default || ""}`.trim() : "");
    if (isLoading) return <div className="p-10 text-white/50 text-sm">Loading player…</div>;
    if (isError || !p) return <div className="p-10 text-white/50 text-sm" data-testid="nhl-player-error">Player not found.</div>;
    const goalie = p.position === "G";
    const cols = goalie ? G_COLS : SK_COLS;
    const fs = p.featuredStats || {};
    const seasons = (p.seasonTotals || []).filter((s) => s.gameTypeId === 2 && (scope === "all" || s.leagueAbbrev === "NHL")).reverse();
    const ht = unit === "ft" ? `${Math.floor(p.heightInInches / 12)}'${p.heightInInches % 12}"` : `${p.heightInCentimeters} cm`;
    const wt = unit === "ft" ? `${p.weightInPounds} lb` : `${p.weightInKilograms} kg`;
    const dd = p.draftDetails;
    const bio = [
        ["Height", ht], ["Weight", wt], ["Born", `${p.birthDate} (Age ${age(p.birthDate)})`],
        ["Birthplace", [t(p.birthCity), t(p.birthStateProvince), p.birthCountry].filter(Boolean).join(", ")],
        [goalie ? "Catches" : "Shoots", p.shootsCatches],
        ["Draft", dd ? `${dd.year} ${dd.teamAbbrev}, Rd ${dd.round}, #${dd.pickInRound} (${dd.overallPick} overall)` : "Undrafted"],
    ];

    return (
        <div className="fade-up pb-16" data-testid="nhl-player-page">
            <div className="relative overflow-hidden border-b border-white/10 bg-[#0B0C13]">
                {p.heroImage && <img src={p.heroImage} alt="" className="absolute inset-0 h-full w-full object-cover object-top opacity-25" />}
                <div className="absolute inset-0 bg-gradient-to-r from-[#0B0C13] via-[#0B0C13]/85 to-transparent" />
                <div className="relative mx-auto max-w-6xl px-4 sm:px-6 py-6 sm:py-10 flex items-end gap-5">
                    <img src={p.headshot} alt={`${t(p.firstName)} ${t(p.lastName)}`} className="h-28 w-28 sm:h-40 sm:w-40 rounded-full bg-white/10 border border-white/15 object-cover" />
                    <div className="min-w-0 pb-2">
                        <Link to="/nhl/rosters" className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-white/50 hover:text-white mb-2"><ArrowLeft className="h-3 w-3" /> Rosters</Link>
                        <div className="text-lg sm:text-2xl font-medium text-white/80 leading-none">{t(p.firstName)}</div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tight text-white leading-none" data-testid="nhl-player-name">{t(p.lastName)}</h1>
                        <div className="mt-3 flex items-center gap-2 flex-wrap text-sm text-white/75">
                            {p.teamLogo && <img src={p.teamLogo} alt="" className="h-7 w-7 object-contain" />}
                            <span className="font-bold">{t(p.fullTeamName) || "Free agent"}</span>
                            {p.sweaterNumber != null && <span className="font-black text-white">#{p.sweaterNumber}</span>}
                            <span className="text-white/50">· {POS[p.position] || p.position}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="mx-auto max-w-6xl px-4 sm:px-6 mt-6 grid gap-5 lg:grid-cols-[320px_1fr]">
                <dl className="rounded-2xl border border-white/10 bg-white/[0.03] p-4 space-y-2.5 h-max" data-testid="nhl-player-bio">
                    {bio.map(([k, v]) => <div key={k} className="flex justify-between gap-3 text-sm"><dt className="text-white/45">{k}</dt><dd className="text-white/90 font-semibold text-right">{v || "—"}</dd></div>)}
                </dl>
                <div className="grid gap-4 sm:grid-cols-2 h-max">
                    <Featured label={`${season(fs.season || 20262027)} Season`} st={fs.regularSeason?.subSeason} goalie={goalie} />
                    <Featured label="NHL Career" st={fs.regularSeason?.career} goalie={goalie} />
                </div>
            </div>

            {!!p.last5Games?.length && (
                <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-8">
                    <h2 className="text-sm font-black uppercase tracking-wider text-white mb-3">Last 5 Games</h2>
                    <StatTable testid="nhl-player-last5" rows={p.last5Games}
                        first={[[(r) => r.gameDate, "Date"], [(r) => `${r.homeRoadFlag === "R" ? "@" : "vs"} ${r.opponentAbbrev}`, "Opp"]]}
                        cols={goalie ? [["decision", "Dec"], ["goalsAgainst", "GA"], ["shotsAgainst", "SA"], ["savePctg", "SV%", G_COLS[6][2]], ["toi", "TOI"]] : [["goals", "G"], ["assists", "A"], ["points", "P"], ["plusMinus", "+/-"], ["shots", "S"], ["pim", "PIM"], ["toi", "TOI"]]} />
                </section>
            )}

            <section className="mx-auto max-w-6xl px-4 sm:px-6 mt-8">
                <div className="flex items-center justify-between gap-3 mb-3">
                    <h2 className="text-sm font-black uppercase tracking-wider text-white">Career Stats · Regular Season</h2>
                    <div className="flex gap-1.5">
                        {[["nhl", "NHL"], ["all", "All Leagues"]].map(([k, l]) => (
                            <button key={k} type="button" onClick={() => setScope(k)} data-testid={`nhl-player-scope-${k}`}
                                className={`px-3 py-1 rounded-lg border text-[11px] font-black uppercase tracking-wider ${scope === k ? "bg-[#9D4CDD] border-[#9D4CDD] text-white" : "border-white/10 text-white/60 hover:text-white"}`}>{l}</button>
                        ))}
                    </div>
                </div>
                <StatTable testid="nhl-player-career" rows={seasons} cols={cols}
                    first={[[(r) => season(r.season), "Season"], [(r) => t(r.teamName), "Team"], ...(scope === "all" ? [[(r) => r.leagueAbbrev, "Lg"]] : [])]} />
            </section>
        </div>
    );
}
